/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [true]
*/

import {
    getFilterElementsDefault,
    Input,
    sharpDateFormatter,
    useErrorState,
    useWorkplaceState,
    WorkplaceBtnActions
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import {
    addLeadgensItem,
    getLeadgenExcel,
    getLeadgens,
    getLeadgensItem,
    statusLeadgensItem,
    updateLeadgensItem
} from 'axios/leadgenApi';
import { getProps } from 'axios/propApi';
import { addReport } from 'axios/reportApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import { subDays } from 'date-fns';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { LeadgenFilter as LeadgenFilterType, LeadgenInfo, LeadgenStat } from 'models/api/leadgen';
import { Prop } from 'models/api/prop';
import { ReportAdd } from 'models/api/report';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Key, useCallback, useState } from 'react';
import { FormatterProps, SummaryFormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import Environment from 'utils/environmentUtils';
import { LeadgenProps } from './Leadgen';
import './Leadgen.scss';
import LeadgenAddButton from './LeadgenAddButton';
import { LeadgenStatsFilter } from './LeadgenFilter';
import LeadgenReportBtn from './LeadgenReportBtn';
import LeadgenModal from './Modal/LeadgenModal';

type statusInfo = {
    id: Key;
    isActive: boolean;
};

type LeadgenStatFormatterProps = FormatterProps<LeadgenStat, LeadgenStat[]>;

type LeadgenStatSummaryFormatterProps = SummaryFormatterProps<LeadgenStat[], LeadgenStat>;

const LeadgenStats = (props: LeadgenProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const { hasRole } = useAuth();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [reportProps, setReportProps] = useState<Prop[]>([]);
    const { handleRequestError } = useErrorState();

    const queryKey = ['LeadgenStats'];

    const statusMutation = useMutation(
        (info: statusInfo) => statusLeadgensItem(api, info.id, info.isActive),
        {
            onSuccess: () => queryClient.invalidateQueries([queryKey]),
            onError: (err: AxiosError<AxiosErrorResponse>) => handleRequestError(err),
        }
    );

    const summaryFormatter = useCallback((key: keyof LeadgenStat) => {
        const component = ({ row }: LeadgenStatSummaryFormatterProps) => <>{row[0][key]}</>;
        return component;
    }, []);

    const idSummaryFormatter = useCallback(() => <>{t('BackOfficeTotal')}:</>, []);

    const isActiveFormatter = useCallback(
        ({ row }: LeadgenStatFormatterProps) => (
            <Input
                type="checkbox"
                name="notUsed"
                checked={row.isActive ?? false}
                onChange={() => {
                    if (row.id != null && row.isActive != null) {
                        return statusMutation.mutate({
                            id: row.id,
                            isActive: !row.isActive,
                        });
                    }
                }}
            />
        ),
        []
    );

    const { isFetching: reportPropsFetching } = useQuery(
        'reportProps',
        () => getProps(api, 'ReportTypeLeadgens', 'Leadgens'),
        {
            onSuccess: (props) => setReportProps(props),
            onError: handleRequestError,
            enabled: !reportProps.length,
        }
    );

    const addReportMutation = useMutation((report: ReportAdd) => addReport(api, report), {
        onSuccess: ({ data }) => {
            window.open(Environment.GetPublicUrl() + '/reportdetails/' + data.id, '_blank');
        },
        onError: (err: AxiosError<AxiosErrorResponse>) => handleRequestError(err),
    });
    
    return (
        <BOWorkplace<LeadgenStat, LeadgenInfo, LeadgenFilterType>
            testId="leadgen-stats"
            filterParams={{
                sd: getStartOfTheDay(subDays(new Date(), 7)),
                ed: getEndOfTheDay(),
                showInactive: false,
                ids: { value: [], array: true },
            }}
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ids',
                    name: t('BackOfficeLeadgens'),
                    formatter: (val) => {
                        if (typeof val == 'object') {
                            if (!props.leadgenListLoading) {
                                return val
                                    .map(
                                        (i) =>
                                            props.leagensList.find((j) => j.id === i.toString())
                                                ?.name
                                    )
                                    .join(', ');
                            } else {
                                return val.join(', ');
                            }
                        }
                        return '';
                    },
                },
                {
                    id: 'showInactive',
                    formatter: (val) => {
                        if (val) {
                            return t('BackOfficeShowInactive');
                        }
                        return '';
                    },
                },
            ])}
            fetchExcelQueryFn={(filter) => getLeadgenExcel(api, filter)}
            modal={{
                component: isModalVisible && (
                    <LeadgenModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getLeadgensItem(api, id),
                changeMutationFn: (item) => updateLeadgensItem(api, item),
                addMutationFn: (item) => addLeadgensItem(api, item),
            }}
            showModal={hasRole('leadgenGetById') ? () => setIsModalVisible(true) : undefined}
            buttons={
                hasRole('leadgenCreate')
                    ? [<LeadgenAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            showExcelBtn={hasRole('leadgenExcel')}
            filter={<LeadgenStatsFilter {...props} />}
            fetchQueryFn={(filter) => getLeadgens(api, filter)}
            queryKey={queryKey}
            queryClient={queryClient}
            pageTitle="BackOfficeLeadgen"
            storagePath="leadgenStats/columns"
            showSummaryRows
            urlFilterKeyPrefix="leadgenStats"
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                    summaryCellClass: 'leadgen__table-summary leadgen__table-summary-header-column',
                    summaryFormatter: idSummaryFormatter,
                    colSpan: (args) => {
                        if (args.type === 'SUMMARY') {
                            return 4;
                        }
                    },
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 50,
                    minWidth: 50,

                    resizable: false,
                    notHidden: true,
                    editable: true,
                    formatter: isActiveFormatter,
                },
                {
                    key: 'name',
                    name: <Trans i18nKey="BackOfficeName" />,
                },
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" />, width: 350 },

                {
                    key: 'clickCount',
                    name: <Trans i18nKey="BackOfficeLeadgenClicks" />,
                    summaryFormatter: summaryFormatter('clickCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'ordersAllCount',
                    name: <Trans i18nKey="BackOfficeOrders" />,
                    summaryFormatter: summaryFormatter('ordersAllCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'ordersApprovedCount',
                    name: <Trans i18nKey="BackOfficeApproved" />,
                    summaryFormatter: summaryFormatter('ordersApprovedCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'ordersGoodshipCount',
                    name: <Trans i18nKey="BackOfficeGoodship" />,
                    summaryFormatter: summaryFormatter('ordersGoodshipCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'ordersDeclinedCount',
                    name: <Trans i18nKey="BackOfficeDeclined" />,
                    summaryFormatter: summaryFormatter('ordersDeclinedCountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'appsNew',
                    name: <Trans i18nKey="BackOfficeAppsNew" />,
                    summaryFormatter: summaryFormatter('appsNewTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'appsNewToAllOrders',
                    name: <Trans i18nKey="BackOfficeAppsNewToAllOrders" />,
                    summaryCellClass: 'leadgen__table-summary',
                    tooltipText: t('BackOfficeAppsNewToAllOrdersNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToAppsNew',
                    name: <Trans i18nKey="BackOfficeLoansToAppsNew" />,
                    summaryCellClass: 'leadgen__table-summary',
                    tooltipText: t('BackOfficeLoansToAppsNewNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'approvedToAppsNew',
                    name: <Trans i18nKey="BackOfficeApprovedToAppsNew" />,
                    summaryCellClass: 'leadgen__table-summary',
                    tooltipText: t('BackOfficeApprovedToAppsNewNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToApproved',
                    name: <Trans i18nKey="BackOfficeLoansToApproved" />,
                    summaryCellClass: 'leadgen__table-summary',
                    tooltipText: t('BackOfficeLoansToApprovedNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'loansToClicks',
                    name: <Trans i18nKey="BackOfficeLoansToClicks" />,
                    summaryCellClass: 'leadgen__table-summary',
                    tooltipText: t('BackOfficeLoansToClicksNote'),
                    tooltipAlwaysShow: true,
                },
                {
                    key: 'postbackSent',
                    name: <Trans i18nKey="BackOfficePostbackSent" />,
                    summaryFormatter: summaryFormatter('postbackSentTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'tariffAmount',
                    name: <Trans i18nKey="BackOfficeTariffAmount" />,
                    summaryFormatter: summaryFormatter('tariffAmountTotal'),
                    summaryCellClass: 'leadgen__table-summary',
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    clickDisable: true,
                    formatter: ({ row }) => (
                        <WorkplaceBtnActions>
                            {hasRole('reportsGetDetails') && (
                                <LeadgenReportBtn
                                    key={`delete-btn-${row.id}`}
                                    id={row.id}
                                    reportProps={reportProps}
                                    reportPropsFetching={reportPropsFetching}
                                    addReportFn={(report) => addReportMutation.mutate(report)}
                                />
                            )}
                        </WorkplaceBtnActions>
                    ),
                },
            ]}
        />
    );
};

const useLeadgenStatsWorkplaceState = () => useWorkplaceState<LeadgenStat, LeadgenInfo>();

export { useLeadgenStatsWorkplaceState };
export default LeadgenStats;
